// External Libraries
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

// Assets
import Filter from '../assets/filter.png';
import DisabledFilter from '../assets/disable-filter.png';

// Styles
import '../styles/Dashboard.css';

// Redux/State Management
import { RootState } from '../redux/store';

// API Services
import { getAllIdeasFromAPI } from '../api/IdeaService';
import { getRecommendedIdeas } from '../api/RecommendationService';
import { getUsersHavingIdeas } from '../api/UserService';

// Types
import {
  FilterUsersResponse,
  IdeaFilterParams,
  IdeaListResponse,
  Idea,
} from '../types/types';

// Utilities
import { formatDate, getSupportNames } from '../utils/CommonUtils';

// Components
import IdeaCard from './common/IdeaCard';
import Loading from './common/Loading';
import IdeasFilter from './IdeasFilter';
import Button from './common/Button';
import Pagination from './common/Pagination';
import Text from './common/Text';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  const user = useSelector((state: RootState) => state.user);
  const reduxDepartments = useSelector(
    (state: RootState) => state.departments,
  ).departments;
  const reduxSchools = useSelector((state: RootState) => state.schools).schools;
  const reduxSeekings = useSelector(
    (state: RootState) => state.support_or_partnership,
  ).support_or_partnership;

  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [message, setMessage] = useState<string | null>(null);
  const [isFilterModel, setIsFilterModel] = useState<boolean | null>(null);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize] = useState<number>(10);

  const [ideasFilter, setIdeasFilter] = useState<IdeaFilterParams>({
    sort: 'desc',
    following: null,
    support_id: [],
    school_id: [],
    department_id: [],
    idea_owner_id: [],
  });
  const [isClearFilter, setIsClearFilter] = useState<boolean>(false);
  const [activeFilterNames, setActiveFilterNames] = useState<string[] | null>(
    null,
  );

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const pageNumber = queryParams.get('page');
  const [following, setFollowing] = useState<string | null>(
    new URLSearchParams(location.search).get('following'),
  );
  const [supportIds, setSupportIds] = useState<string | null>(
    new URLSearchParams(location.search).get('support_id'),
  );
  const [schoolId, setSchoolId] = useState<string | null>(
    new URLSearchParams(location.search).get('school_id'),
  );
  const [departmentId, setDepartmentId] = useState<string | null>(
    new URLSearchParams(location.search).get('department_id'),
  );
  const [ideaOwnerId, setIdeaOwnerId] = useState<string | null>(
    new URLSearchParams(location.search).get('idea_owner_id'),
  );
  const [sortOrder, setSortOrder] = useState<string | null>(
    new URLSearchParams(location.search).get('sort_order'),
  );

  const initialPage = pageNumber ? Number(pageNumber) : 1;
  const [currentPage, setCurrentPage] = useState<number>(Number(initialPage));
  const [isFilteredView, setIsFilteredView] = useState<boolean>(false);
  const [ideaOwnerFilter, setIdeaOwnerFilter] = useState<string | null>(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    const page = queryParams.get('page');
    if (page) {
      setCurrentPage(Number(page));
    }

    setFollowing(queryParams.get('following'));
    setSupportIds(queryParams.get('support_id'));
    setSchoolId(queryParams.get('school_id'));
    setDepartmentId(queryParams.get('department_id'));
    setIdeaOwnerId(queryParams.get('idea_owner_id'));
    setSortOrder(queryParams.get('sort_order'));

    if (
      !queryParams.get('following') &&
      !queryParams.get('support_id') &&
      !queryParams.get('school_id') &&
      !queryParams.get('department_id') &&
      !queryParams.get('idea_owner_id') &&
      !queryParams.get('sort_order')
    ) {
      setIsClearFilter(false);
      setIsFilteredView(false);
    }
  }, [location.search]);

  const updateActiveFilters = (
    prevState: string[] | null,
    filterValue: string | null,
  ): string[] => {
    if (filterValue && !prevState?.includes(filterValue)) {
      return [...(prevState ?? []), filterValue];
    }
    return prevState ?? [];
  };

  useEffect(() => {
    if (!pageNumber) {
      setCurrentPage(initialPage);
    }

    let updatedFilters: string[] = [];

    if (sortOrder !== null) {
      setIsClearFilter(true);
      const followingSoryByValue =
        sortOrder === 'asc' ? 'Most Recent Last' : 'Most Recent First';
      updatedFilters = updateActiveFilters(
        updatedFilters,
        followingSoryByValue,
      );
    }

    if (following !== null) {
      setIsClearFilter(true);
      const followingFilterValue =
        following === 'true' ? 'Following' : 'Not following';
      updatedFilters = updateActiveFilters(
        updatedFilters,
        followingFilterValue,
      );
    }

    if (supportIds !== null && supportIds.length !== 0) {
      setIsClearFilter(true);
      const supportIdsArray = supportIds
        .split(',')
        .map((id) => Number(id.trim()));
      supportIdsArray.forEach((id) => {
        const supportFilterValue =
          reduxSeekings.find((support) => support.id === id)?.name || null;
        if (supportFilterValue) {
          updatedFilters = updateActiveFilters(
            updatedFilters,
            supportFilterValue,
          );
        }
      });
    }

    if (schoolId !== null) {
      setIsClearFilter(true);
      const schoolFilterValue =
        reduxSchools.find((school) => school.id === Number(schoolId))?.name ||
        null;
      updatedFilters = updateActiveFilters(updatedFilters, schoolFilterValue);
    }

    if (departmentId !== null) {
      setIsClearFilter(true);
      const departmentFilterValue =
        reduxDepartments.find(
          (department) => department.id === Number(departmentId),
        )?.name || null;
      updatedFilters = updateActiveFilters(
        updatedFilters,
        departmentFilterValue,
      );
    }

    if (ideaOwnerId !== null) {
      setIsClearFilter(true);
      const fetchFilterUsers = async () => {
        const filterUsersResponse = await getUsersHavingIdeas(null, null);
        if ('code' in filterUsersResponse) {
          console.error('Error fetching filter users:', filterUsersResponse);
        } else {
          const ideaOwner = (
            filterUsersResponse as FilterUsersResponse
          ).users.find((user) => Number(ideaOwnerId) === user.id);
          if (ideaOwner) setIdeaOwnerFilter(ideaOwner.name);
        }
      };
      fetchFilterUsers();
    }
    if (ideaOwnerFilter) {
      updatedFilters = updateActiveFilters(updatedFilters, ideaOwnerFilter);
    }
    setActiveFilterNames(updatedFilters);
  }, [
    pageNumber,
    initialPage,
    sortOrder,
    following,
    supportIds,
    schoolId,
    departmentId,
    ideaOwnerId,
    ideaOwnerFilter,
  ]);

  useEffect(() => {
    const fetchIdeas = async () => {
      await getAllIdeas();
    };

    fetchIdeas();
  }, [currentPage, ideasFilter, isFilteredView]);

  useEffect(() => {
    if (isFilterModel) {
      document.body.classList.add('locked-scroll');
    } else {
      document.body.classList.remove('locked-scroll');
    }
    return () => {
      document.body.classList.remove('locked-scroll');
    };
  }, [isFilterModel]);

  const getAllIdeas = async () => {
    setLoading(true);
    if (user.userDetails?.userId !== undefined) {
      try {
        const result =
          isFilteredView ||
          supportIds !== null ||
          following !== null ||
          schoolId !== null ||
          departmentId !== null ||
          ideaOwnerId !== null ||
          sortOrder !== null
            ? await getAllIdeasFromAPI(
                user.userDetails.userId,
                currentPage,
                pageSize,
                sortOrder === 'asc' || sortOrder === 'desc'
                  ? sortOrder
                  : (ideasFilter.sort ?? 'desc'),
                following !== null
                  ? following === 'true'
                  : ideasFilter.following,
                supportIds !== null
                  ? supportIds.split(',').map((id) => Number(id.trim()))
                  : (ideasFilter.support_id ?? 0),
                schoolId !== null
                  ? [Number(schoolId)]
                  : (ideasFilter.school_id ?? 0),
                departmentId !== null
                  ? [Number(departmentId)]
                  : (ideasFilter.department_id ?? 0),
                ideaOwnerId !== null
                  ? [Number(ideaOwnerId)]
                  : (ideasFilter.idea_owner_id ?? undefined),
              )
            : await getRecommendedIdeas(
                user.userDetails.userId,
                currentPage,
                pageSize,
              );

        if ('code' in result) {
          console.error('Error fetching ideas:', result);
          if (result.code === 'NOT_FOUND') {
            if (currentPage > 1) {
              setMessage(null);
              setCurrentPage(1);
              handlePageChange(1);
            } else {
              setMessage(result.message);
            }
          } else {
            setMessage(result.message);
          }
          setLoading(false);
        } else {
          if ((result as IdeaListResponse).ideas.length > 0) {
            setIdeas((result as IdeaListResponse).ideas);
            setLoading(false);
            setCurrentPage((result as IdeaListResponse).page);
            setTotalPages((result as IdeaListResponse).total_pages);
            setMessage(null);
          } else {
            console.log('ideas array is empty');
            setMessage(null);
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Catch Error fetching ideas:', error);
        setLoading(false);
      }
    }
  };

  const convertToQueryString = (params: IdeaFilterParams): string => {
    const queryParams = new URLSearchParams();

    if (params.following !== null) {
      queryParams.set('following', params.following.toString());
    }

    const appendArrayParam = (
      key: string,
      value: string[] | number[] | null,
    ) => {
      if (value && value.length > 0) {
        queryParams.set(key, value.join(','));
      }
    };

    const appendSingleParam = (
      key: string,
      value: string | number | null | undefined,
    ) => {
      if (value !== null && value !== undefined) {
        queryParams.set(key, value.toString());
      }
    };

    appendArrayParam('support_id', params.support_id);
    appendArrayParam('school_id', params.school_id);
    appendArrayParam('department_id', params.department_id);
    appendArrayParam('idea_owner_id', params.idea_owner_id);
    appendSingleParam('sort_order', params.sort);

    return `${queryParams.toString()}`;
  };

  const filterIdeas = (ideas: IdeaFilterParams) => {
    setIdeasFilter(ideas);
    setIsFilterModel(false);
    setCurrentPage(1);
    setIsClearFilter(true);
    setLoading(true);
    setIsFilteredView(true);
    const url = convertToQueryString(ideas);
    if (url) navigate(`/dashboard?` + url);
  };

  const handleClearFilters = () => {
    setIsFilteredView(false);
    setIdeasFilter({
      sort: 'desc',
      following: null,
      support_id: [],
      school_id: [],
      department_id: [],
      idea_owner_id: [],
    });
    setActiveFilterNames(null);
    setIsClearFilter(false);
    setCurrentPage(1);
    setLoading(true);

    setFollowing(null);
    setSupportIds(null);
    setSchoolId(null);
    setDepartmentId(null);
    setIdeaOwnerId(null);
    setSortOrder(null);
    setIdeaOwnerFilter(null);

    navigate(`/dashboard`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setLoading(true);
    const url =
      `/dashboard?page=${page}` +
      (following !== null ? `&following=${following}` : '') +
      (supportIds !== null
        ? `&support_id=${encodeURIComponent(supportIds)}`
        : '') +
      (schoolId !== null ? `&school_id=${schoolId}` : '') +
      (departmentId !== null ? `&department_id=${departmentId}` : '') +
      (ideaOwnerId !== null ? `&idea_owner_id=${ideaOwnerId}` : '') +
      (sortOrder !== null ? `&sort_order=${sortOrder}` : '');
    navigate(url);
  };

  return (
    <>
      {loading ? (
        <div className="loading-class">
          <Loading color="var(--black)" />
        </div>
      ) : message ? (
        <div className="loading-class">{message}</div>
      ) : ideas.length > 0 ? (
        <div className="grid gap-10">
          <div className="feed-header-container">
            <div className="sub-header-container">
              <Text className="sub-header">Ideas</Text>
              <Button
                type="button"
                className={
                  isClearFilter === true && activeFilterNames !== null
                    ? 'disabled-filter-button'
                    : 'filter-button'
                }
                onClick={() => setIsFilterModel(true)}
                disabled={isClearFilter === true}
              >
                {isClearFilter === true && activeFilterNames !== null ? (
                  <img
                    src={DisabledFilter}
                    alt="DisabledFilter"
                    className="filter-image"
                  />
                ) : (
                  <img src={Filter} alt="Filter" className="filter-image" />
                )}
              </Button>
            </div>
            {isClearFilter && activeFilterNames && (
              <>
                {activeFilterNames && (
                  <div className="active-filters">
                    {activeFilterNames.map((filter, index) => (
                      <Button
                        type="button"
                        key={index}
                        className="role-button selected"
                      >
                        {filter}
                      </Button>
                    ))}
                    <Button
                      type="button"
                      onClick={() => handleClearFilters()}
                      className="clear-filter-button"
                    >
                      Clear Filters
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
          {ideas.length > 0 &&
            ideas.map((idea: Idea) => (
              <IdeaCard
                id={idea.id}
                title={idea.title as string}
                ideaOwner={`${idea.principal_investigator.first_name} ${idea.principal_investigator.last_name}`}
                ideaOwnerPhoto={idea.principal_investigator.profile_image_url}
                postedDate={idea.created_at && formatDate(idea.created_at)}
                key={idea.id}
                isMyIdea={false}
                seeking={
                  Array.isArray(idea.support_ids) && idea.support_ids.length > 0
                    ? getSupportNames(idea.support_ids, reduxSeekings)
                    : []
                }
                schoolName={
                  reduxSchools.find(
                    (s) => s.id === idea.principal_investigator.school_id,
                  )?.name ?? ''
                }
                departmentName={
                  reduxDepartments.find(
                    (d) => d.id === idea.principal_investigator.department_id,
                  )?.name ?? ''
                }
                isFollowed={idea.is_following}
                ideaOwnerId={idea.principal_investigator.id}
              />
            ))}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      ) : (
        <div className="loading-class">No ideas found on this page.</div>
      )}
      {isFilterModel && (
        <IdeasFilter
          toggleFilter={() => setIsFilterModel(!isFilterModel)}
          filterIdeas={filterIdeas}
        />
      )}
    </>
  );
};

export default Dashboard;
